import React, { useState, useEffect, useContext, useMemo } from 'react';
import './CreateAvatar.css';
// We will replace the import of static styles
// import STYLE_PROMPTS from '../../data/stylePrompts';
import api from '../../api';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../Login/AuthContext';
import { useToast } from '../UI/ToastContext';

const CreateAvatar = ({ onStylesSelected, preselectedStyles = [], userId = 1 }) => {
  const [selectedStyles, setSelectedStyles] = useState(preselectedStyles);
  const [styles, setStyles] = useState([]);
  const [styleMap, setStyleMap] = useState({});
  const [activeTab, setActiveTab] = useState('all');
  const [totalTokenCost, setTotalTokenCost] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [referencePhotos, setReferencePhotos] = useState([]);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(true);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate(); // Hook for programmatic navigation
  const { showSuccess, showError } = useToast();

  // Memorize unique categories extracted from styles
  const categories = useMemo(() => {
    if (!styles.length) return [];
    
    // Extract unique categories
    const uniqueCategories = [...new Set(styles.map(style => style.category))];
    return uniqueCategories;
  }, [styles]);

  // Load styles from API instead of static file
  useEffect(() => {
    const fetchStyles = async () => {
      try {
        setIsLoading(true);
        const response = await api.get('/aivatar/styles');
        if (response.data.success) {
          const stylesArray = response.data.styles;
          setStyles(stylesArray);
          
          // Also create a map for easy access by ID
          const stylesById = {};
          stylesArray.forEach(style => {
            // Ensure that ID is a number
            const styleId = parseInt(style.id, 10);
            stylesById[styleId] = style;
          });
          setStyleMap(stylesById);
        }
      } catch (error) {
        console.error('Error loading styles:', error);
        setSubmitError('Unable to load styles. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchStyles();
  }, []);

  // Load reference photos
  useEffect(() => {
    const fetchUserPhotos = async () => {
      try {
        setIsLoadingPhotos(true);
        const response = await api.get('/photos');
        if (response.data.success) {
          setReferencePhotos(response.data.files || []);
        }
      } catch (error) {
        console.error('Error loading photos:', error);
      } finally {
        setIsLoadingPhotos(false);
      }
    };
    
    fetchUserPhotos();
  }, []);

  // Update token cost whenever selected styles change
  useEffect(() => {
    let cost = 0;
    selectedStyles.forEach(styleId => {
      const style = styleMap[styleId];
      if (style) {
        cost += style.token_cost;
      }
    });
    setTotalTokenCost(cost);
    
    // Call the parent component's callback with selected styles
    if (onStylesSelected) {
      onStylesSelected(selectedStyles);
    }
  }, [selectedStyles, onStylesSelected, styleMap]);

  // Check if user has enough tokens
  const hasEnoughTokens = () => {
    if (!user) return false;
    const userTotalTokens = (user.tokens_ltd || 0) + (user.tokens_subscription || 0);
    return userTotalTokens >= totalTokenCost;
  };

  // Check if user has enough reference photos (at least 2)
  const hasEnoughReferencePhotos = () => {
    return referencePhotos.length >= 2;
  };

  const getTokensError = () => {
    if (!user) return "Unable to verify token balance";
    const userTotalTokens = (user.tokens_ltd || 0) + (user.tokens_subscription || 0);
    return `Not enough tokens: you need ${totalTokenCost} but only have ${userTotalTokens} available.`;
  };

  const handleStyleToggle = (styleId) => {
    // Ensure styleId is a number
    const numericStyleId = parseInt(styleId, 10);
    
    setSelectedStyles(prev => {
      if (prev.includes(numericStyleId)) {
        return prev.filter(id => id !== numericStyleId);
      } else {
        return [...prev, numericStyleId];
      }
    });
  };

  const getStylesToDisplay = () => {
    if (activeTab === 'all') {
      return styles;
    }
    // Filter by active category
    return styles.filter(style => style.category === activeTab);
  };

  const getStyleById = (styleId) => {
    return styleMap[styleId];
  };

  const handleSubmit = async () => {
    if (selectedStyles.length === 0) {
      setSubmitError("Please select at least one style");
      return;
    }

    // Check if user has enough reference photos
    if (!hasEnoughReferencePhotos()) {
      setSubmitError("You need at least 2 reference photos to create avatars");
      return;
    }

    // Check if user has enough tokens before submitting
    if (!hasEnoughTokens()) {
      setSubmitError(getTokensError());
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Ensure all IDs are numbers
      const numericStyleIds = selectedStyles.map(id => parseInt(id, 10));
      
      // Create data for the request
      const data = {
        styles: numericStyleIds,
        style_prompts: numericStyleIds.map(styleId => {
          const style = styleMap[styleId];
          return style ? style.prompt : '';
        }),
        use_reference_elements: true
      };

      console.log("Sending style IDs:", numericStyleIds);
      
      const response = await api.post('/aivatar/create_tasks_batch', data);
      
      // Use an English message
      showSuccess(`${response.data.message || 'Avatar generation tasks created successfully!'}`);
      
      // Redirect to gallery after successful generation
      navigate('/app/gallery');
      
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.error === "Not enough tokens") {
        setSubmitError(`${error.response.data.message}. Please deselect some styles or purchase more tokens.`);
        showError(`${error.response.data.message}. Please deselect some styles or purchase more tokens.`);
      } else {
        setSubmitError(error.response?.data?.error || error.message);
        showError(error.response?.data?.error || error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="create-avatar">
      <div className="create-avatar__main">
        <div className="create-avatar__header">
          <h2>Create Your Avatar</h2>
          <p>Choose up to 10 styles to apply to your avatar</p>
        </div>

        <div className="create-avatar__tabs">
          <button 
            className={`create-avatar__tab ${activeTab === 'all' ? 'active' : ''}`} 
            onClick={() => setActiveTab('all')}
          >
            All Styles
          </button>
          
          {/* Generate a tab for each category */}
          {categories.map(category => (
            <button
              key={category}
              className={`create-avatar__tab ${activeTab === category ? 'active' : ''}`}
              onClick={() => setActiveTab(category)}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="create-avatar__loading">
            <p>Loading styles...</p>
          </div>
        ) : (
          <div className="create-avatar__grid">
            {getStylesToDisplay().map(style => (
              <div 
                key={style.id} 
                className={`create-avatar__item ${selectedStyles.includes(style.id) ? 'selected' : ''}`}
                onClick={() => handleStyleToggle(style.id)}
              >
                <div className="create-avatar__image-container">
                  <img 
                    src={style.image_url || `${process.env.PUBLIC_URL}/images/placeholder.jpg`} 
                    alt={style.name} 
                    className="create-avatar__image" 
                  />
                  {selectedStyles.includes(style.id) && (
                    <div className="create-avatar__selected-overlay">
                      <span className="create-avatar__checkmark">✓</span>
                    </div>
                  )}
                </div>
                <div className="create-avatar__details">
                  <h3 className="create-avatar__name">{style.name}</h3>
                  <p className="create-avatar__description">{style.description}</p>
                  <p className="create-avatar__token-cost">{style.token_cost} {style.token_cost === 1 ? 'token' : 'tokens'}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="create-avatar__sidebar">
        <div className="create-avatar__summary-panel panel panel-with-header">
          <div className="panel-header">
            <h3>Selected Styles</h3>
          </div>
          <div className="panel-content">
            {selectedStyles.length === 0 ? (
              <p className="create-avatar__empty-message">No styles selected yet</p>
            ) : (
              <>
                <div className="create-avatar__selected-table">
                  <div className="create-avatar__selected-header">
                    <div className="create-avatar__style-name">Style</div>
                    <div className="create-avatar__token-amount">Cost</div>
                    <div className="create-avatar__action-col"></div>
                  </div>
                  <ul className="create-avatar__selected-list">
                    {selectedStyles.map(styleId => {
                      const style = getStyleById(styleId);
                      return style ? (
                        <li key={styleId} className="create-avatar__selected-item">
                          <div className="create-avatar__style-name" title={style.name}>
                            {style.name}
                          </div>
                          <div className="create-avatar__token-amount">
                            {style.token_cost} {style.token_cost === 1 ? 'token' : 'tokens'}
                          </div>
                          <div className="create-avatar__action-col">
                            <button 
                              className="btn-icon create-avatar__remove-btn" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStyleToggle(styleId);
                              }}
                              title="Remove style"
                              aria-label={`Remove ${style.name}`}
                            >
                              <span>×</span>
                            </button>
                          </div>
                        </li>
                      ) : null;
                    })}
                  </ul>
                </div>
                
                <div className="create-avatar__total">
                  <span>Total</span>
                  <span className={!hasEnoughTokens() ? 'token-error' : ''}>
                    {totalTokenCost} tokens
                  </span>
                </div>

                {!hasEnoughTokens() && (
                  <div className="create-avatar__token-warning">
                    <p>You don't have enough tokens for these styles.</p>
                    <div className="create-avatar__token-actions">
                      <span>Please </span>
                      <button 
                        className="btn-text"
                        onClick={() => setSelectedStyles([])}
                      >
                        deselect styles
                      </button>
                      <span> or </span>
                      <Link to="/pricing" className="btn-text">
                        purchase more tokens
                      </Link>
                    </div>
                  </div>
                )}

                {!hasEnoughReferencePhotos() && (
                  <div className="create-avatar__token-warning">
                    <p>You need at least 2 reference photos.</p>
                    <div className="create-avatar__token-actions">
                      <span>Please </span>
                      <Link to="/app/upload" className="btn-text">
                        upload more photos
                      </Link>
                    </div>
                  </div>
                )}

                {submitError && (
                  <p className="create-avatar__error text-center">{submitError}</p>
                )}

                <div className="panel-footer text-center">
                  {isLoadingPhotos ? (
                    <p>Loading photos...</p>
                  ) : hasEnoughReferencePhotos() ? (
                    <button 
                      className="btn btn-primary btn-full"
                      onClick={handleSubmit}
                      disabled={isSubmitting || selectedStyles.length === 0 || !hasEnoughTokens()}
                    >
                      {isSubmitting ? 'Creating Avatars...' : 'Create Avatars'}
                    </button>
                  ) : (
                    <Link 
                      to="/app/upload" 
                      className="btn btn-primary btn-full"
                    >
                      Upload Reference Photos
                    </Link>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAvatar; 